import React, { forwardRef } from 'react';
import { useMoney } from '../../hooks/useMoney';
import './ui.css';

/**
 * Drop-in replacement for `<input type="number">` on currency/money fields
 * (cost, budget, quote totals, payment amounts, unit prices, etc).
 *
 * Contract-compatible with a plain number input: `value`/`onChange` behave
 * exactly like `<input type="number">` — the value passed to `onChange` is
 * the raw event, same as before, so existing `parseFloat`/`Number(...)`
 * handlers keep working unchanged. The currency symbol is a
 * purely visual prefix, never baked into the value.
 *
 * Optionally renders a label/error/help block matching FormField.tsx's
 * `Input` component, so it can also replace `<FormInput type="number">`
 * usages. When no `label`/`error`/`help` is passed, it renders just the
 * wrapper + input, making it a safe drop-in wherever the surrounding
 * `form-group`/`label` markup already exists at the call site.
 */
export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Currency symbol shown as a visual prefix. Defaults to the project currency's symbol (blank when not set). */
  symbol?: string;
  label?: string;
  error?: string;
  help?: string;
  required?: boolean;
  /** Class applied to the outer wrapper (the flex row holding symbol + input). */
  wrapperClassName?: string;
  /** Style applied to the outer wrapper, not the inner input — a column
   *  width from a table layout (e.g. LineItemsEditor's columnStyle) has to
   *  land on the flex item the row actually measures, not the text field
   *  nested inside it. Every other field type in that table gets its width
   *  this way; currency previously had to rely on a second, hand-matched
   *  CSS rule instead, which is exactly the kind of two-sources-of-truth
   *  setup that drifts silently. */
  wrapperStyle?: React.CSSProperties;
}

/** A complete or partially-typed number: '', '1', '1.', '1.50', '-2'. */
const NUMERIC_RE = /^-?\d*\.?\d*$/;

const stripCommas = (v: string): string => v.replace(/,/g, '');

/**
 * Adds thousands separators to the integer part only, preserving whatever
 * the user is mid-way through typing — a trailing '.' and any number of
 * decimal places are left exactly as entered, so formatting never fights
 * the cursor. Non-numeric values pass through untouched.
 */
export function formatWithCommas(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const raw = stripCommas(String(value));
  if (!NUMERIC_RE.test(raw)) return String(value);

  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const [intPart, decPart] = unsigned.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${decPart !== undefined ? `.${decPart}` : ''}`;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      symbol: symbolProp,
      label,
      error,
      help,
      required,
      disabled,
      className = '',
      wrapperClassName = '',
      wrapperStyle,
      id,
      ...props
    },
    ref
  ) => {
    const money = useMoney();
    const symbol = symbolProp ?? money.symbol;
    const fieldId = id ?? (label ? `field-${Math.random().toString(36).substr(2, 9)}` : undefined);
    const inputClassName = className || 'form-input';
    const { value, onChange, ...rest } = props;

    // Rendered as type="text" rather than type="number" purely so the value
    // can carry thousands separators — a number input silently refuses to
    // display them, which left large amounts as unreadable digit runs
    // (185000000) next to read-only totals that were comma-formatted.
    // inputMode="decimal" keeps the numeric keypad on mobile.
    const display = formatWithCommas(value);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const clean = stripCommas(e.target.value);
      // Reject anything that isn't a partial number, so the field can never
      // hold a value that parseFloat would silently truncate.
      if (clean !== '' && clean !== '-' && !NUMERIC_RE.test(clean)) return;
      // Consumers parse `e.target.value` with parseFloat/Number, so hand
      // them the unformatted value — the commas are presentation only.
      e.target.value = clean;
      onChange?.(e);
    };

    const field = (
      <div className={`currency-input-wrapper${error ? ' has-error' : ''} ${wrapperClassName}`.trim()} style={wrapperStyle}>
        <span className="currency-symbol">{symbol}</span>
        <input
          ref={ref}
          id={fieldId}
          type="text"
          inputMode="decimal"
          value={display}
          onChange={handleChange}
          disabled={disabled}
          className={`no-spinner ${inputClassName}${error ? ' has-error' : ''}`.trim()}
          {...rest}
        />
      </div>
    );

    if (!label && !error && !help) {
      return field;
    }

    return (
      <div className="form-group">
        {label && (
          <label className="form-label" htmlFor={fieldId}>
            {label}
            {required && <span className="form-required">*</span>}
          </label>
        )}
        {field}
        {error && <span className="form-error-text">{error}</span>}
        {!error && help && <span className="form-helper">{help}</span>}
      </div>
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';
